import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useFitCount } from "./useFitCount";
import "./paged-board.css";

type Props<Item> = {
  items: Item[];
  getKey: (item: Item) => string | number;
  renderItem: (item: Item) => ReactNode;
  /** True while the server holds more than has been fetched, and asking for it is allowed. */
  canFetchMore: boolean;
  fetching: boolean;
  onFetchMore: () => void;
  /** Names the list for a screen reader, e.g. "Updates". */
  label: string;
  newerLabel: string;
  olderLabel: string;
  itemMinWidthRem: number;
  itemMinHeightRem: number;
  maxColumns: number;
};

const GAP_REM = 1;

/**
 * A list that never scrolls. It shows as many items as its space holds, newest first, and
 * pages through the rest with two buttons. When the visitor pages past what has been
 * fetched, it asks for the next page from the server.
 */
export function PagedBoard<Item>(props: Props<Item>) {
  const { items, canFetchMore, fetching, onFetchMore } = props;
  const grid = useRef<HTMLOListElement>(null);
  const fit = useFitCount(grid, {
    minWidthRem: props.itemMinWidthRem,
    minHeightRem: props.itemMinHeightRem,
    gapRem: GAP_REM,
    maxColumns: props.maxColumns,
  });
  const perPage = fit.columns * fit.rows;
  // The first item on show is remembered, not the page number, so that resizing the window
  // keeps the visitor's place instead of jumping them to whatever page 3 has become.
  const [firstIndex, setFirstIndex] = useState(0);
  const start = Math.floor(firstIndex / perPage) * perPage;
  const shown = items.slice(start, start + perPage);
  const shortOfAPage = start + perPage > items.length;

  useEffect(() => {
    if (shortOfAPage && canFetchMore && !fetching) onFetchMore();
  }, [shortOfAPage, canFetchMore, fetching, onFetchMore]);

  const hasNewer = start > 0;
  const hasOlder = start + perPage < items.length || canFetchMore;
  const paged = hasNewer || hasOlder;

  return (
    <div className="paged-board" data-slot="stage">
      <ol
        ref={grid}
        className="paged-board__grid"
        aria-label={props.label}
        style={
          {
            "--board-columns": String(fit.columns),
            "--board-rows": String(fit.rows),
            "--board-gap": `${String(GAP_REM)}rem`,
          } as CSSProperties
        }
      >
        {shown.map((item) => (
          <li key={props.getKey(item)} className="paged-board__cell">
            {props.renderItem(item)}
          </li>
        ))}
      </ol>
      {paged ? (
        <nav className="paged-board__pager" aria-label={`${props.label}: pages`}>
          <button
            type="button"
            className="paged-board__step"
            onClick={() => setFirstIndex(Math.max(start - perPage, 0))}
            disabled={!hasNewer}
          >
            <span aria-hidden="true">&larr;&nbsp;</span>
            {props.newerLabel}
          </button>
          <p className="paged-board__place" role="status">
            {shown.length > 0
              ? `${String(start + 1)} to ${String(start + shown.length)}`
              : "Loading"}
          </p>
          <button
            type="button"
            className="paged-board__step"
            onClick={() => setFirstIndex(start + perPage)}
            disabled={!hasOlder || fetching}
          >
            {fetching ? "Loading" : props.olderLabel}
            <span aria-hidden="true">&nbsp;&rarr;</span>
          </button>
        </nav>
      ) : null}
    </div>
  );
}
